import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await axios.get(`${API}/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") { setState("paid"); return; }
        if (attempts >= 6) { setState("pending"); return; }
        setTimeout(poll, 1800);
      } catch {
        if (attempts >= 6) { setState("error"); return; }
        setTimeout(poll, 1800);
      }
    };
    poll();
  }, [sessionId]);

  return (
    <Shell testid="payment-success-page">
      {state === "checking" && (
        <>
          <p className="label text-[#C5A059] mb-4">One moment</p>
          <h1 className="font-display text-5xl text-[#FAF7F2]">Confirming your payment…</h1>
        </>
      )}
      {state === "paid" && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <p className="label text-[#C5A059] mb-4">Confirmed</p>
          <h1 className="font-display text-5xl sm:text-6xl text-[#FAF7F2] leading-tight">Thank you.</h1>
          <p className="mt-6 text-[#FAF7F2]/70 text-lg max-w-md">
            Your payment was successful. I'll be in touch personally to schedule your session. Thank you for trusting Jessica Bacon.
          </p>
        </motion.div>
      )}
      {state === "pending" && (
        <>
          <p className="label text-[#C5A059] mb-4">Almost there</p>
          <h1 className="font-display text-5xl text-[#FAF7F2]">Your payment is processing.</h1>
          <p className="mt-6 text-[#FAF7F2]/70 text-lg max-w-md">We'll email you as soon as it's confirmed.</p>
        </>
      )}
      {state === "error" && (
        <>
          <p className="label text-[#C5A059] mb-4">Hmm</p>
          <h1 className="font-display text-5xl text-[#FAF7F2]">We couldn't verify that session.</h1>
          <p className="mt-6 text-[#FAF7F2]/70 text-lg max-w-md">If you were charged, don't worry — reach out and I'll confirm right away.</p>
        </>
      )}
      <HomeLink />
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell testid="payment-cancel-page">
      <p className="label text-[#C5A059] mb-4">No charge made</p>
      <h1 className="font-display text-5xl sm:text-6xl text-[#FAF7F2] leading-tight">Checkout cancelled.</h1>
      <p className="mt-6 text-[#FAF7F2]/70 text-lg max-w-md">
        Nothing was charged. Whenever you're ready, your first session is only a click away.
      </p>
      <HomeLink />
    </Shell>
  );
}

function Shell({ children, testid }) {
  return (
    <main data-testid={testid} className="min-h-screen bg-[#18181B] grain flex items-center">
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-14 w-full">{children}</div>
    </main>
  );
}

function HomeLink() {
  return (
    <Link data-testid="back-home-link" to="/" className="mt-10 inline-flex rounded-full bg-[#C5A059] text-[#18181B] px-8 py-4 label hover:bg-[#FAF7F2] transition-colors">
      ← Back to home
    </Link>
  );
}
